import Button from "components/Button";
import { Grid } from "components/Grid/Grid";
import { Loader } from "components/Loader/Loader.component";
import Modal from "components/Modal";
import Text from "components/Text";
import TextInput from "components/TextInput";
import PageContent from "pages/PageContent";
import {
    ProfileContentWrapper,
    StraightLine,
    SubHeading,
} from "pages/PageContent/PageContent.styled";
import React from "react";
import { BiEditAlt } from "react-icons/bi";
import { BsFillTrashFill } from "react-icons/bs";
import theme from "theme";
import {
    ButtonContainer,
    ExpRole,
    ExpTitle,
    GreyText,
    MobileExpWrapper,
    TBody,
    THead,
    Table,
    Td,
    Th,
    Tr,
} from "./ExperienceDetails.styled";
import { useExperienceDetails } from "./useExperienceDetails";

export const ExperienceDetails: React.FC = React.memo(() => {
    const {
        expInputs,
        handleInputChange,
        inputErrors,
        handleExpEdit,
        isLoading,
        data,
        receivedExp,
        handleSubmit,
        handleExpDelete,
        editId,
        handleNavigation,
        isModalOpen,
        isFetching,
        toggleModal,
        modalConfirmAction,
        windowSize,
    } = useExperienceDetails();
    return (
        <PageContent>
            <ProfileContentWrapper>
                {isFetching && <Loader size={28} />}
                <Text
                    variant="heading5"
                    fontWeight="700"
                    color={theme.colors.grey.dark}
                >
                    Complete Your Profile (4/7)
                </Text>
                <SubHeading variant="label">
                    ADD EXPERIENCE <StraightLine />
                </SubHeading>
                <Grid
                    row={true}
                    expanded
                    marginTop={2}
                    columnGap={1}
                    rowGap={1}
                >
                    <TextInput
                        name="category"
                        type="text"
                        label="Category"
                        required
                        placeholder="Ex. Acting"
                        labelColor={theme.colors.text}
                        minWidth="160px"
                        value={expInputs.category}
                        errorMsg={inputErrors.category}
                        onChange={handleInputChange}
                    />
                    <TextInput
                        name="year"
                        type="number"
                        label="Year"
                        required
                        placeholder="Ex. 2016"
                        labelColor={theme.colors.text}
                        minWidth="160px"
                        value={expInputs.year}
                        errorMsg={inputErrors.year}
                        onChange={handleInputChange}
                    />
                </Grid>
                <Grid
                    row={true}
                    expanded
                    marginTop={2}
                    columnGap={1}
                    rowGap={1}
                >
                    <TextInput
                        name="nameOfProduction"
                        type="text"
                        label="Name of Production"
                        required
                        placeholder="Ex. Disney"
                        labelColor={theme.colors.text}
                        minWidth="160px"
                        value={expInputs.nameOfProduction}
                        errorMsg={inputErrors.nameOfProduction}
                        onChange={handleInputChange}
                    />
                    <TextInput
                        name="roleDetails"
                        type="text"
                        label="Role Details"
                        required
                        placeholder="Ex. Rebellious teen"
                        labelColor={theme.colors.text}
                        minWidth="180px"
                        value={expInputs.roleDetails}
                        errorMsg={inputErrors.roleDetails}
                        onChange={handleInputChange}
                    />
                </Grid>
                <Grid
                    row={true}
                    expanded
                    marginTop={2}
                    columnGap={1}
                    rowGap={1}
                >
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={() => handleNavigation("Back")}
                    >
                        Back
                    </Button>
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={handleSubmit}
                        isLoading={isLoading}
                    >
                        {!!editId ? "Edit" : "Add"}
                    </Button>
                    <Button
                        type="button"
                        variant="filled"
                        onClick={() => handleNavigation("Next")}
                    >
                        Next
                    </Button>
                </Grid>
                {receivedExp && data.length > 0 && windowSize.width > 425 && (
                    <Table>
                        <THead>
                            <Tr>
                                <Th>Category</Th>
                                <Th>Year</Th>
                                <Th>Name of Production</Th>
                                <Th>Role Details</Th>
                                <Th>Action</Th>
                            </Tr>
                        </THead>
                        <TBody>
                            {data.map((item: any) => (
                                <Tr key={item.id}>
                                    <Td>{item.category}</Td>
                                    <Td>{item.year}</Td>
                                    <Td>{item.name_of_production}</Td>
                                    <Td>{item.role_details}</Td>
                                    <Td>
                                        <BiEditAlt
                                            title="Edit Experience"
                                            color="blue"
                                            onClick={() => handleExpEdit(item)}
                                        />
                                        <BsFillTrashFill
                                            title="Delete Experience"
                                            color="red"
                                            onClick={() =>
                                                handleExpDelete(item.id)
                                            }
                                        />
                                    </Td>
                                </Tr>
                            ))}
                        </TBody>
                    </Table>
                )}
                {receivedExp && data.length > 0 && windowSize.width < 425 && (
                    <Grid wrap="wrap" rowGap={1} columnGap={1} marginTop={1}>
                        {data.map((item: any) => (
                            <MobileExpWrapper>
                                <Text variant="body2" fontWeight="600">
                                    Acting{" "}
                                    <GreyText variant="label">(2016)</GreyText>
                                </Text>
                                <ExpTitle variant="body2">
                                    {item.role_details}
                                </ExpTitle>
                                <ExpRole variant="body2">
                                    {item.name_of_production}
                                </ExpRole>
                                <ButtonContainer>
                                    <BiEditAlt
                                        title="Edit Experience"
                                        color="blue"
                                        onClick={() => handleExpEdit(item)}
                                    />
                                    <BsFillTrashFill
                                        title="Delete Experience"
                                        color="red"
                                        onClick={() => handleExpDelete(item.id)}
                                    />
                                </ButtonContainer>
                            </MobileExpWrapper>
                        ))}
                    </Grid>
                )}
            </ProfileContentWrapper>
            <Modal isOpen={isModalOpen} onClose={toggleModal}>
                <Modal.Header>
                    <Text variant="heading3">Confirm</Text>
                </Modal.Header>
                <Modal.Content>
                    <Text variant="body1">
                        Are you sure you want to leave? Any unsaved changes will
                        be lost.
                    </Text>
                </Modal.Content>
                <Modal.Footer>
                    <Grid
                        row={true}
                        expanded
                        marginTop={2}
                        columnGap={1}
                        rowGap={1}
                    >
                        <Button
                            type="button"
                            variant="outlined"
                            onClick={toggleModal}
                        >
                            No
                        </Button>
                        <Button
                            type="button"
                            variant="filled"
                            onClick={modalConfirmAction}
                        >
                            Yes
                        </Button>
                    </Grid>
                </Modal.Footer>
            </Modal>
        </PageContent>
    );
});

ExperienceDetails.displayName = "ExperienceDetails";
